import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { GanttChart } from "@/components/timeline/GanttChart";
import { BoardKpiCards, isAtRisk } from "@/components/board/BoardKpiCards";
import { useProjectDependencies } from "@/hooks/useProjectDependencies";
import { usePortfolio } from "@/hooks/usePortfolio";
import { byRank } from "@/lib/priorityTier";
import type { Project } from "@/types/project";

/**
 * Executive — the Top 10 at a glance (Brett's overhaul, 2026-09-18).
 *
 * Read-only, board-meeting altitude: the board's four scoreboard cards
 * scoped to the TOP 10 ONLY (Brett, 2026-09-21), then ONE Gantt of the Top 10 in rank
 * order — bars colored by health, today line, dependency arrows.
 * Nothing is editable here; the board (/top-projects) is where rows
 * get managed. Quick milestones were tried as ticks on the bars and
 * pulled the same day (Brett: 70 dots read as noise) — they live on
 * the project page only.
 *
 * Retired the same day: the Initiative Scoreboard cards, the Key
 * Initiatives / Projects Gantt views with their sponsor + PM filters,
 * and the Pipeline Capacity Outlook heatmap. Key Initiatives left the
 * UI entirely (2026-10-26); /capacity is the one capacity surface.
 */
export function Executive() {
  const portfolio = usePortfolio(false);
  const depsQ = useProjectDependencies();
  const [atRiskOnly, setAtRiskOnly] = useState(false);

  const projects: Project[] = useMemo(
    () => (Array.isArray(portfolio.data) ? portfolio.data : []),
    [portfolio.data],
  );

  // Top 10 only — the cards and the Gantt read the same rows (Brett,
  // 2026-09-21: "the KPI cards at the top reflect only the top 10").
  // /top-projects keeps its board-wide cards.
  const top10All = useMemo(
    () =>
      projects.filter(
        (p) => p.priority_tier === "top10" && (p.status ?? "").toLowerCase() !== "complete",
      ),
    [projects],
  );

  const top10 = useMemo(
    () => top10All.filter((p) => !atRiskOnly || isAtRisk(p)).sort(byRank),
    [top10All, atRiskOnly],
  );
  const dated = useMemo(() => top10.filter((p) => p.start_date && p.end_date), [top10]);
  const undated = useMemo(() => top10.filter((p) => !p.start_date || !p.end_date), [top10]);

  // Governance window: this calendar year through the end of next.
  const { rangeStart, rangeEnd } = useMemo(() => {
    const y = new Date().getFullYear();
    return { rangeStart: new Date(y, 0, 1), rangeEnd: new Date(y + 1, 11, 31) };
  }, []);

  const isLoading = portfolio.isLoading;

  return (
    <>
      <TopBar
        title="Executive"
        subtitle="The Top 10 at a glance — health, timeline, and benefit. Manage the board on Top Projects."
      />
      <div className="space-y-6 p-8">
        {isLoading ? (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading executive summary...
          </div>
        ) : (
          <>
            <BoardKpiCards
              rows={top10All}
              atRiskOnly={atRiskOnly}
              onToggleAtRisk={() => setAtRiskOnly((v) => !v)}
              scopeLabel="in the Top 10"
            />

            <section data-testid="top10-gantt" className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-ink-muted">
                    Top 10 Timeline
                  </h2>
                  <span className="rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-ink-muted">
                    {top10.length}
                  </span>
                  {atRiskOnly && (
                    <span className="text-[11px] text-amber-700 dark:text-amber-300">red / yellow only</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <Link
                    to="/weekly-update"
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-navy-600 dark:text-brand-navy-400 hover:underline"
                  >
                    Weekly update
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                  <Link
                    to="/top-projects"
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-navy-600 dark:text-brand-navy-400 hover:underline"
                  >
                    Manage the board
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
              <p className="text-xs text-ink-muted">
                Rank order, bars colored by health. Arrows show which projects wait on another.
                Click a row for its milestones and go-to-green plans.
              </p>

              {dated.length === 0 ? (
                <div className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-ink-subtle">
                  {atRiskOnly
                    ? "No red or yellow Top 10 projects with dates."
                    : "No Top 10 projects with dates yet — manage the board on Top Projects."}
                </div>
              ) : (
                <GanttChart
                  projects={dated}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  zoom="weeks"
                  showMilestones={false}
                  dependencies={depsQ.data ?? []}
                />
              )}

              {/* Rows without dates can't draw a bar — list them so the
                  ranked Top 10 is always complete on this surface. */}
              {undated.length > 0 && (
                <div className="rounded-lg border border-line bg-surface p-3">
                  <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
                    Not yet scheduled
                  </div>
                  <ul className="space-y-1">
                    {undated.map((p) => (
                      <li key={p.id} className="flex items-baseline gap-2 text-sm">
                        <span className="tabular-nums text-ink-subtle">
                          #{p.priority_rank != null ? Math.round(p.priority_rank) : "—"}
                        </span>
                        <Link
                          to={`/portfolio/${p.id}`}
                          className="font-medium text-ink hover:text-brand-navy-600 dark:hover:text-brand-navy-400 hover:underline"
                        >
                          {p.name}
                        </Link>
                        <span className="text-[11px] text-ink-subtle">{p.id}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
